import {
  LegalDocLayout,
  LP, LH3, LUl, LLi, LNote, LTable,
  type LegalSection,
} from './LegalDocLayout';

const sections: LegalSection[] = [
  {
    id: 'what-are-cookies',
    heading: 'What Are Cookies?',
    body: (
      <>
        <LP>
          Cookies are small text files placed on your device (computer, phone, or tablet) when
          you visit a website. They are widely used to make websites work efficiently, to remember
          your preferences, and to provide information to site owners about how their site is used.
        </LP>
        <LP>
          In addition to cookies, we use other similar tracking technologies on our websites and
          platform:
        </LP>
        <LUl>
          <LLi>
            <strong>Local storage:</strong> Browser-based storage that persists across sessions,
            used for UI state and preferences (e.g., sidebar collapsed/expanded, theme
            preference).
          </LLi>
          <LLi>
            <strong>Session storage:</strong> Temporary storage cleared when you close your
            browser tab, used for wizard state and form data.
          </LLi>
          <LLi>
            <strong>Pixel tags (web beacons):</strong> Tiny transparent images embedded in
            emails or pages that confirm delivery and, where consented, track interactions.
          </LLi>
          <LLi>
            <strong>SDK identifiers:</strong> Anonymous device identifiers used in mobile
            integrations for session attribution.
          </LLi>
        </LUl>
        <LP>
          This Cookie Policy applies to all cookies and similar technologies used on{' '}
          <strong>gograbjob.com</strong>, <strong>app.gograbjob.com</strong>, and all associated
          subdomains operated by Zykes Technologies LLP.
        </LP>
      </>
    ),
  },
  {
    id: 'why-we-use',
    heading: 'Why We Use Cookies',
    body: (
      <>
        <LP>We use cookies and similar technologies to:</LP>
        <LUl>
          <LLi>
            <strong>Make the Services work:</strong> Maintain your login session, protect against
            cross-site request forgery (CSRF), and ensure pages load correctly.
          </LLi>
          <LLi>
            <strong>Remember your preferences:</strong> Store your language setting, theme (light
            / dark), and UI layout choices between visits.
          </LLi>
          <LLi>
            <strong>Understand how you use our Services:</strong> Collect aggregate analytics to
            identify which features are used, where users encounter difficulties, and how we can
            improve performance.
          </LLi>
          <LLi>
            <strong>Measure marketing effectiveness:</strong> With your consent, attribute website
            visits and conversions to our advertising campaigns so we can optimise our marketing
            spend.
          </LLi>
          <LLi>
            <strong>Personalise your experience:</strong> With your consent, tailor content and
            offers shown on our marketing pages to your browsing behaviour and interests.
          </LLi>
        </LUl>
        <LNote>
          Cookies placed within the GoGrabJob Business application (app.gograbjob.com) are
          strictly necessary or performance-only. Marketing cookies are only used on our
          marketing and public-facing pages (gograbjob.com/business), never inside the
          authenticated product.
        </LNote>
      </>
    ),
  },
  {
    id: 'cookie-categories',
    heading: 'Cookie Categories',
    body: (
      <>
        <LP>
          We classify our cookies into four categories based on their purpose:
        </LP>

        <LH3>Category 1 — Strictly Necessary</LH3>
        <LP>
          These cookies are essential for the Services to function and cannot be switched off in
          our systems. They are usually set in response to actions you take, such as logging in,
          setting your privacy preferences, or filling in forms. Without these cookies, parts of
          the website or platform will not work.
        </LP>
        <LTable
          headers={['Cookie Name', 'Purpose', 'Duration', 'First / Third Party']}
          rows={[
            ['ggj_session', 'Authenticated user session token', 'Session (cleared on logout)', 'First party'],
            ['ggj_csrf', 'CSRF protection token to prevent cross-site request forgery', 'Session', 'First party'],
            ['ggj_auth_state', 'OAuth 2.0 state parameter for SSO flows', 'Session', 'First party'],
            ['ggj_consent', 'Stores your cookie consent preferences', '12 months', 'First party'],
            ['ggj_theme', 'Stores your light/dark mode preference', '12 months', 'First party'],
          ]}
        />

        <LH3>Category 2 — Performance & Functionality</LH3>
        <LP>
          These cookies help us understand how visitors interact with our Services and remember
          preferences that affect functionality but are not strictly necessary. Data collected is
          aggregated and anonymised where possible.
        </LP>
        <LTable
          headers={['Cookie Name', 'Purpose', 'Duration', 'First / Third Party']}
          rows={[
            ['ggj_perf_sess', 'Tracks page load performance and feature latency for debugging', '30 minutes', 'First party'],
            ['ggj_ui_state', 'Remembers sidebar, panel, and column layout preferences', '90 days', 'First party'],
            ['ggj_lang', 'Stores your preferred display language', '12 months', 'First party'],
            ['ggj_tour_state', 'Tracks which onboarding tour steps you have completed', '90 days', 'First party'],
          ]}
        />

        <LH3>Category 3 — Analytics</LH3>
        <LP>
          With your consent, these cookies allow us to measure and analyse how visitors use our
          website — which pages are visited most, where users click, and where drop-off occurs.
          This helps us make evidence-based improvements. Analytics data is processed in aggregate
          and is not used to personally identify you.
        </LP>
        <LTable
          headers={['Provider', 'Cookie(s)', 'Purpose', 'Duration']}
          rows={[
            ['PostHog (self-hosted)', '_ph_id, _ph_session', 'Product analytics — feature usage, funnel analysis, session replay (PII masked)', '1 year / Session'],
            ['Google Analytics 4', '_ga, _ga_*', 'Website traffic, acquisition source, page engagement', '2 years / 1 year'],
            ['Hotjar', '_hjid, _hjSession*', 'Heatmaps and scroll maps on marketing pages (no recordings in authenticated app)', '1 year / 30 min'],
          ]}
        />

        <LH3>Category 4 — Marketing & Advertising</LH3>
        <LP>
          With your consent, these cookies are used to track visitors across websites and to
          display relevant advertising based on your browsing profile. They help us measure the
          effectiveness of our campaigns. You can opt out at any time using our consent manager.
        </LP>
        <LTable
          headers={['Provider', 'Cookie(s)', 'Purpose', 'Duration']}
          rows={[
            ['LinkedIn Insight Tag', 'li_sugr, UserMatchHistory, bcookie', 'LinkedIn campaign attribution and retargeting to professionals in HR/TA', '90 days – 2 years'],
            ['Google Ads', '_gcl_au, IDE', 'Google remarketing and conversion tracking', '90 days – 2 years'],
            ['Meta Pixel', '_fbp, _fbc', 'Facebook/Instagram campaign tracking and lookalike audiences', '90 days – 2 years'],
          ]}
        />
        <LNote>
          Marketing cookies are never set inside the authenticated GoGrabJob Business application.
          They are limited to gograbjob.com marketing pages. You will not see advertising
          follow-up within the product based on your usage of the platform.
        </LNote>
      </>
    ),
  },
  {
    id: 'third-party-cookies',
    heading: 'Third-Party Services & Embedded Content',
    body: (
      <>
        <LP>
          Some pages on our marketing site embed content from third-party services (e.g., YouTube
          videos, Calendly booking widgets, Intercom live chat). When you interact with such
          embedded content, the third party may place their own cookies on your device. These
          cookies are governed by the respective third-party privacy policies:
        </LP>
        <LUl>
          <LLi>
            <strong>Intercom (customer support chat):</strong>{' '}
            <a
              href="https://www.intercom.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              intercom.com/legal/privacy
            </a>
          </LLi>
          <LLi>
            <strong>Calendly (demo booking):</strong>{' '}
            <a
              href="https://calendly.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              calendly.com/privacy
            </a>
          </LLi>
          <LLi>
            <strong>YouTube (embedded videos):</strong>{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              policies.google.com/privacy
            </a>
          </LLi>
          <LLi>
            <strong>Stripe (payment processing):</strong>{' '}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              stripe.com/privacy
            </a>
          </LLi>
        </LUl>
        <LP>
          We do not share personal data from our authenticated application with advertising
          networks or data brokers.
        </LP>
      </>
    ),
  },
  {
    id: 'retention',
    heading: 'Cookie Retention Periods',
    body: (
      <>
        <LP>
          Cookies fall into two categories based on how long they persist on your device:
        </LP>
        <LUl>
          <LLi>
            <strong>Session cookies:</strong> Temporary cookies that are automatically deleted
            when you close your browser. We use these for authentication tokens and OAuth state
            management.
          </LLi>
          <LLi>
            <strong>Persistent cookies:</strong> Cookies that remain on your device until they
            expire or you delete them. Our persistent cookies have durations ranging from 30
            minutes (performance) to 2 years (analytics and marketing), as detailed in the tables
            above.
          </LLi>
        </LUl>
        <LP>
          The retention periods stated above are the maximum lifetimes. Cookies set with your
          consent are automatically deleted if you withdraw consent through our cookie settings
          panel.
        </LP>
      </>
    ),
  },
  {
    id: 'managing-preferences',
    heading: 'Managing Your Cookie Preferences',
    body: (
      <>
        <LH3>6.1 Cookie Settings Panel</LH3>
        <LP>
          You can view and update your cookie preferences at any time using our Cookie Settings
          panel, accessible by clicking "Cookie Settings" in the footer of any GoGrabJob marketing
          page. Changes take effect immediately — consented cookies are removed within seconds of
          withdrawal.
        </LP>

        <LH3>6.2 Browser Controls</LH3>
        <LP>
          All major browsers allow you to control cookies through their settings:
        </LP>
        <LUl>
          <LLi>
            <strong>Google Chrome:</strong> Settings → Privacy and security → Cookies and other
            site data
          </LLi>
          <LLi>
            <strong>Mozilla Firefox:</strong> Preferences → Privacy & Security → Cookies and Site
            Data
          </LLi>
          <LLi>
            <strong>Apple Safari:</strong> Preferences → Privacy → Manage Website Data
          </LLi>
          <LLi>
            <strong>Microsoft Edge:</strong> Settings → Site permissions → Cookies and site data
          </LLi>
        </LUl>
        <LP>
          Please note that blocking or deleting strictly necessary cookies will impair the
          functionality of the GoGrabJob Business application (e.g., you will not be able to
          remain logged in).
        </LP>

        <LH3>6.3 Opt-Out Links for Analytics & Advertising</LH3>
        <LUl>
          <LLi>
            <strong>Google Analytics:</strong>{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
          </LLi>
          <LLi>
            <strong>LinkedIn:</strong>{' '}
            <a
              href="https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              LinkedIn Retargeting Opt-out
            </a>
          </LLi>
          <LLi>
            <strong>Google Ads:</strong> Visit{' '}
            <a
              href="https://adssettings.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              adssettings.google.com
            </a>
          </LLi>
          <LLi>
            <strong>Meta (Facebook):</strong>{' '}
            <a
              href="https://www.facebook.com/settings?tab=ads"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              Facebook Ad Preferences
            </a>
          </LLi>
          <LLi>
            <strong>Industry-wide opt-outs (US/EU):</strong>{' '}
            <a
              href="https://optout.aboutads.info"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              optout.aboutads.info
            </a>{' '}
            and{' '}
            <a
              href="https://www.youronlinechoices.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              youronlinechoices.eu
            </a>
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'do-not-track',
    heading: 'Do Not Track',
    body: (
      <>
        <LP>
          Some browsers include a "Do Not Track" (DNT) setting that signals to websites that you
          prefer not to be tracked across sites. At present, there is no universally accepted
          standard for how websites should respond to DNT signals.
        </LP>
        <LP>
          We currently do not alter our cookie behaviour in response to DNT signals. However, you
          can achieve equivalent results by disabling analytics and marketing cookies through our
          Cookie Settings panel, as described in Section 6.
        </LP>
        <LP>
          We support the Global Privacy Control (GPC) signal as an opt-out of the sale/sharing of
          personal data under CCPA for California residents. When a valid GPC signal is detected,
          we will treat it as equivalent to withdrawing consent for marketing cookies.
        </LP>
      </>
    ),
  },
  {
    id: 'policy-updates',
    heading: 'Updates to This Cookie Policy',
    body: (
      <>
        <LP>
          We update this Cookie Policy periodically to reflect changes in the cookies and
          technologies we use, or changes in applicable law. When we make material changes, we
          will:
        </LP>
        <LUl>
          <LLi>Update the "Last updated" date at the top of this page.</LLi>
          <LLi>Re-display the consent banner to obtain fresh consent where required.</LLi>
          <LLi>
            For significant changes affecting essential cookies, notify customers via in-platform
            notification and email.
          </LLi>
        </LUl>
        <LP>
          We recommend reviewing this page periodically. Your continued use of our Services after
          a cookie policy update constitutes acceptance of the changes.
        </LP>
      </>
    ),
  },
  {
    id: 'contact',
    heading: 'Contact',
    body: (
      <>
        <LP>
          If you have questions about our use of cookies or wish to exercise your rights regarding
          cookie-related personal data, please contact:
        </LP>
        <LP>
          <strong>Zykes Technologies LLP</strong>
          <br />
          Email:{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>
          <br />
          Subject line: "Cookie Policy Enquiry"
        </LP>
        <LP>
          For requests relating to your GDPR rights in connection with cookie data, please also
          refer to our{' '}
          <a
            href="/business/gdpr-compliance"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            GDPR Compliance
          </a>{' '}
          page.
        </LP>
      </>
    ),
  },
];

export function CookiePolicyPage() {
  return (
    <LegalDocLayout
      category="Legal"
      title="Cookie Policy"
      subtitle="How GoGrabJob Business uses cookies and similar tracking technologies — what they do, why we use them, and how to control them."
      effectiveDate="1 April 2026"
      lastUpdated="14 April 2026"
      version="1.2"
      sections={sections}
      contactEmail="privacy@gograbjob.com"
    />
  );
}
